import { AppointmentStatus } from '@dataTypes/appointment.type'
import type { AppointmentResponse } from '@dataTypes/appointment.type'
import { mapProviderLabelToScheduleCardColor } from '@components/ui/ScheduleCard/ScheduleCard.types'
import type {
  SchedulerApptStatus,
  SchedulerColumn,
  SchedulerEvent,
  SchedulerProviderOption,
  SchedulerViewMode,
  SchedulerBlockRange,
} from '@components/ui/Scheduler/Scheduler.types'
import {
  SCHEDULER_DEMO_BLOCK_RANGES,
  SCHEDULER_DEMO_COLUMNS,
  SCHEDULER_DEMO_PROVIDERS,
  SCHEDULER_STATUS_BADGE_COLOR,
  createSchedulerDemoEvents,
} from '@components/ui/Scheduler/Scheduler.types'
import {
  isAiReceptionistAppointment,
  aiReceptionistActorHint,
} from '@utils/aiReceptionist.util'

const DS_SCHEDULER_STORAGE_KEY = 'use_ds_scheduler'

/** Enable with `?dsScheduler=1` or `localStorage.use_ds_scheduler = '1'`. */
export const isDsSchedulerEnabled = (
  search = window.location.search,
): boolean => {
  const params = new URLSearchParams(search)
  const fromQuery = params.get('dsScheduler')
  if (fromQuery === '1' || fromQuery === 'true') {
    try {
      localStorage.setItem(DS_SCHEDULER_STORAGE_KEY, '1')
    } catch {
      /* ignore */
    }
    return true
  }
  if (fromQuery === '0' || fromQuery === 'false') {
    try {
      localStorage.removeItem(DS_SCHEDULER_STORAGE_KEY)
    } catch {
      /* ignore */
    }
    return false
  }
  try {
    return localStorage.getItem(DS_SCHEDULER_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

export const mapAppointmentStatusToScheduler = (
  status: number,
): SchedulerApptStatus | undefined => {
  switch (status) {
    case AppointmentStatus.Confirmed:
      return 'Confirmed'
    case AppointmentStatus.Unconfirmed:
      return 'Unconfirmed'
    case AppointmentStatus.Late:
      return 'Late'
    case AppointmentStatus.NoShow:
      return 'No-show'
    case AppointmentStatus.CheckIn:
      return 'Check-in'
    case AppointmentStatus.InTreatment:
      return 'In-treatment'
    case AppointmentStatus.CheckOut:
      return 'Check-out'
    case AppointmentStatus.Completed:
      return 'Complete'
    default:
      return undefined
  }
}

const providerDisplayName = (appointment: AppointmentResponse): string => {
  const first = appointment.provider?.first_name?.trim() ?? ''
  const last = appointment.provider?.last_name?.trim() ?? ''
  const full = `${first} ${last}`.trim()
  if (!full) return 'Unassigned'
  return full.startsWith('Dr.') ? full : `Dr. ${full}`
}

const patientDisplayName = (
  appointment: AppointmentResponse,
  isHipaa: boolean,
): string => {
  if (isHipaa) return '••••••••'
  const first = appointment.patient?.first_name?.trim() ?? ''
  const last = appointment.patient?.last_name?.trim() ?? ''
  return `${first} ${last}`.trim() || 'Unknown patient'
}

const isBlockAppointment = (appointment: AppointmentResponse): boolean =>
  Boolean((appointment as AppointmentResponse & { IsBlock?: boolean }).IsBlock)

export const mapAppointmentToSchedulerEvent = (
  appointment: AppointmentResponse,
  options?: {
    isHipaa?: boolean
    clinicName?: string
    aiUserId?: string | null
  },
): SchedulerEvent | null => {
  if (isBlockAppointment(appointment)) return null

  const start = new Date(
    appointment.appointment_start_date ||
      `${appointment.appt_date} ${appointment.appt_start}`,
  )
  const end = new Date(
    appointment.appointment_end_date ||
      `${appointment.appt_date} ${appointment.appt_end}`,
  )
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null

  const status = mapAppointmentStatusToScheduler(appointment.appt_status)
  const providerName = providerDisplayName(appointment)
  const patientName = patientDisplayName(appointment, Boolean(options?.isHipaa))
  const color = mapProviderLabelToScheduleCardColor(
    appointment.provider?.provider_color?.label,
  )

  const preferredDentist =
    appointment.patient?.preferred_provider_info ||
    appointment.patient?.preferredProvider
  const preferredHygienist =
    appointment.patient?.preferred_hygienist_info ||
    appointment.patient?.preferredHygienist

  return {
    id: appointment.appointment_id || String(appointment.Id),
    columnId: appointment.operatory_id,
    start,
    end,
    patientName,
    appointmentType: appointment.appointmenttype?.name,
    color,
    providerId: appointment.provider_id,
    providerName,
    status,
    aiAssisted: isAiReceptionistAppointment(appointment, options?.aiUserId),
    aiHint:
      aiReceptionistActorHint(appointment, options?.aiUserId, 'appointment') ??
      undefined,
    info: {
      patientName,
      provider: providerName,
      clinic: options?.clinicName || undefined,
      procedure: appointment.appointmenttype?.name,
      dateTime: start.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }),
      status,
      statusColor: status ? SCHEDULER_STATUS_BADGE_COLOR[status] : undefined,
      preferredDentist: preferredDentist
        ? `${preferredDentist.first_name ?? ''} ${preferredDentist.last_name ?? ''}`.trim()
        : '-',
      preferredHygienist: preferredHygienist
        ? `${preferredHygienist.first_name ?? ''} ${preferredHygienist.last_name ?? ''}`.trim()
        : '-',
      isInsured: appointment.is_insured ? 'Yes' : 'No',
      note: appointment.notes?.[0]?.body || '-',
      preAuthStatus: appointment.pre_auth_status || undefined,
    },
  }
}

export const mapAppointmentsToSchedulerEvents = (
  appointments: AppointmentResponse[] | null | undefined,
  options?: {
    isHipaa?: boolean
    clinicName?: string
    aiUserId?: string | null
  },
): SchedulerEvent[] => {
  if (!appointments?.length) return []
  return appointments
    .map((appointment) => mapAppointmentToSchedulerEvent(appointment, options))
    .filter((event): event is SchedulerEvent => Boolean(event))
}

export const mapOperatoriesToSchedulerColumns = (
  operatories: Array<{ operatory_id: string; name: string }> | null | undefined,
): SchedulerColumn[] => {
  if (!operatories?.length) return []
  return operatories.map((operatory) => ({
    id: operatory.operatory_id,
    label: operatory.name,
  }))
}

export const mapProvidersToSchedulerOptions = (
  providers:
    | Array<{
        provider_id: string
        first_name?: string
        last_name?: string
      }>
    | null
    | undefined,
): SchedulerProviderOption[] => {
  if (!providers?.length) return []
  return providers.map((provider) => {
    const full =
      `${provider.first_name ?? ''} ${provider.last_name ?? ''}`.trim()
    return {
      id: provider.provider_id,
      name: full ? (full.startsWith('Dr.') ? full : `Dr. ${full}`) : 'Provider',
    }
  })
}

export const resolveSchedulerViewMode = (
  viewMode?: string | null,
): SchedulerViewMode => (viewMode === 'Full' ? 'Full' : 'Fixed')

export type SchedulerDataBundle = {
  events: SchedulerEvent[]
  columns: SchedulerColumn[]
  providers: SchedulerProviderOption[]
  blockRanges: SchedulerBlockRange[]
  usingFallback: boolean
}

/**
 * Prefer live appointments/operatories/providers; fall back to demo seeds when
 * the API list is empty or failed so frontend-only work still shows a board.
 */
export const resolveSchedulerData = (input: {
  appointments: AppointmentResponse[] | null | undefined
  operatories: Array<{ operatory_id: string; name: string }> | null | undefined
  providers:
    | Array<{
        provider_id: string
        first_name?: string
        last_name?: string
      }>
    | null
    | undefined
  date: Date
  isHipaa?: boolean
  clinicName?: string
  fetchError?: string | null
  aiUserId?: string | null
}): SchedulerDataBundle => {
  const liveEvents = mapAppointmentsToSchedulerEvents(input.appointments, {
    isHipaa: input.isHipaa,
    clinicName: input.clinicName,
    aiUserId: input.aiUserId,
  })
  const liveColumns = mapOperatoriesToSchedulerColumns(input.operatories)
  const liveProviders = mapProvidersToSchedulerOptions(input.providers)

  // Prefer live events; if none (API down / empty / frontend-only), seed demo
  // so the board is still reviewable.
  const usingFallback = liveEvents.length === 0

  return {
    events: usingFallback ? createSchedulerDemoEvents(input.date) : liveEvents,
    columns: liveColumns.length ? liveColumns : SCHEDULER_DEMO_COLUMNS,
    providers: liveProviders.length ? liveProviders : SCHEDULER_DEMO_PROVIDERS,
    blockRanges: SCHEDULER_DEMO_BLOCK_RANGES,
    usingFallback,
  }
}
